import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import AppLayout from "../components/layout/AppLayout";
import BreadcrumbNav from "../components/layout/BreadcrumbNav";
import type { Scale } from "../types";
import { apiClient } from "../utils/api";
import { useWebSocket } from "../utils/useWebSocket";

const ScalesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingScale, setEditingScale] = useState<Scale | null>(null);
  const [realtimeWeights, setRealtimeWeights] = useState<
    Map<string, { weight: number; stable: boolean; timestamp: number }>
  >(new Map());
  const [form] = Form.useForm();

  const {
    data: scales = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["scales"],
    queryFn: () => apiClient.getScales(),
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<Scale>) => apiClient.createScale(data),
    onSuccess: () => {
      message.success("Scale created successfully");
      queryClient.invalidateQueries({ queryKey: ["scales"] });
      setModalVisible(false);
    },
    onError: (error) => {
      console.error("Failed to create scale:", error);
      message.error("Failed to save scale");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Scale> }) =>
      apiClient.updateScale(id, data),
    onSuccess: () => {
      message.success("Scale updated successfully");
      queryClient.invalidateQueries({ queryKey: ["scales"] });
      setModalVisible(false);
    },
    onError: (error) => {
      console.error("Failed to update scale:", error);
      message.error("Failed to save scale");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteScale(id),
    onSuccess: () => {
      message.success("Scale deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["scales"] });
    },
    onError: (error) => {
      console.error("Failed to delete scale:", error);
      message.error("Failed to delete scale");
    },
  });

  // Initialize WebSocket for real-time updates
  const { connected, connectionStatus, subscribe } = useWebSocket(undefined, {
    deviceType: 'scale',
    onMessage: (msg) => {
      if (
        msg.type === "weight_update" &&
        msg.device_id &&
        msg.weight !== undefined
      ) {
        setRealtimeWeights((prev) => {
          const newMap = new Map(prev);
          newMap.set(msg.device_id!, {
            weight: msg.weight!,
            stable: msg.stable ?? false,
            timestamp: msg.timestamp ?? Date.now() / 1000,
          });
          return newMap;
        });
      }
    },
    onConnect: () => {
      console.log("Real-time updates connected");
      message.success("Scale real-time updates connected");
    },
    onDisconnect: () => {
      console.log("Real-time updates disconnected");
    },
    onError: () => {
      console.error("WebSocket connection error");
    },
  });

  // Subscribe to all scale devices when connected and scales are loaded
  useEffect(() => {
    if (connected && scales.length > 0) {
      const deviceIds = scales.map((s) => s.device_id);
      subscribe(deviceIds);
    }
  }, [connected, scales, subscribe]);

  const handleEdit = (scale: Scale) => {
    setEditingScale(scale);
    form.setFieldsValue(scale);
    setModalVisible(true);
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleAdd = () => {
    setEditingScale(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingScale) {
        updateMutation.mutate({ id: editingScale.id, data: values });
      } else {
        createMutation.mutate(values);
      }
    } catch (error) {
      // form validation error
    }
  };

  const columns: ColumnsType<Scale> = [
    {
      title: "Device ID",
      dataIndex: "device_id",
      key: "device_id",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string, record) => {
        const realtimeData = realtimeWeights.get(record.device_id);
        const isLive = realtimeData && Date.now() / 1000 - realtimeData.timestamp <= 5;
        const displayStatus = isLive ? "online" : status;
        return (
          <Tag color={displayStatus === "online" ? "green" : "red"}>
            {displayStatus.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Weight",
      key: "weight",
      render: (_, record) => {
        const realtimeData = realtimeWeights.get(record.device_id);
        const displayWeight = realtimeData
          ? realtimeData.weight
          : record.weight;

        return (
          <Space>
            <span>
              {displayWeight} {record.unit}
            </span>
            {realtimeData && realtimeData.stable && (
              <Tag color="green">STABLE</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete this scale?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AppLayout
      title="Scales Management"
      extraActions={
        <Space>
          <Tooltip title={`Real-time updates: ${connectionStatus}`}>
            <div className="flex items-center gap-4">
              <Badge
                status={
                  connectionStatus === "connected"
                    ? "processing"
                    : connectionStatus === "connecting"
                    ? "warning"
                    : "error"
                }
              />{" "}
              {connectionStatus === "connected"
                ? "Live"
                : connectionStatus === "connecting"
                ? "Connecting..."
                : "Offline"}
            </div>
          </Tooltip>
          <Tooltip title="Refresh data">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isFetching}
            />
          </Tooltip>
        </Space>
      }
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <BreadcrumbNav />
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Scale
        </Button>
      </div>
      <Card>
        <Table
          columns={columns}
          dataSource={scales}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 600 }}
        />
      </Card>

      <Modal
        title={editingScale ? "Edit Scale" : "Add Scale"}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="device_id"
            label="Device ID"
            rules={[{ required: true, message: "Please enter device ID" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="offline">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </AppLayout>
  );
};

export default ScalesPage;
